// src/hooks/useAccountListReport.ts
// Data hook for the Account List report.
// Returns the TanStack Query compatible shape so the backend swap is one file.
// Reads URL state flags: ?empty=1, ?loading=1, ?error=1.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { MOCK_ACCOUNT_LIST_ROWS } from '../pages/Accounting/Reports/AccountList/mockData';
import type { AccountListReportData } from '../pages/Accounting/Reports/AccountList/types';

interface UseAccountListReportReturn {
  data: AccountListReportData | undefined;
  loading: boolean;
  error: Error | null;
  refetch: () => void;
  lastRefreshedAt: Date | null;
}

function readCompanyName(): string {
  try {
    const n = localStorage.getItem('company_name');
    if (n && n.trim()) return n;
  } catch (e) {
    // ignore
  }
  return 'Your Company';
}

export function useAccountListReport(): UseAccountListReportReturn {
  const [searchParams] = useSearchParams();
  const forceEmpty = searchParams.get('empty') === '1';
  const forceLoading = searchParams.get('loading') === '1';
  const forceError = searchParams.get('error') === '1';

  const [loading, setLoading] = useState<boolean>(forceLoading);
  const [error, setError] = useState<Error | null>(forceError ? new Error('Report failed to load') : null);
  const [lastRefreshedAt, setLastRefreshedAt] = useState<Date | null>(new Date());

  const data = useMemo<AccountListReportData | undefined>(() => {
    if (loading || error) return undefined;
    return {
      companyName: readCompanyName(),
      reportTitle: 'Account List',
      generatedAt: lastRefreshedAt ?? new Date(),
      rows: forceEmpty ? [] : MOCK_ACCOUNT_LIST_ROWS,
    };
  }, [loading, error, lastRefreshedAt, forceEmpty]);

  const refetch = useCallback(() => {
    setLoading(true);
    setError(null);
    // Simulate a short fetch so the refresh spinner is visible
    setTimeout(function () {
      setLoading(false);
      setLastRefreshedAt(new Date());
    }, 600);
  }, []);

  // Honor live URL changes to the flags
  useEffect(() => {
    setLoading(forceLoading);
    setError(forceError ? new Error('Report failed to load') : null);
  }, [forceLoading, forceError]);

  return { data, loading, error, refetch, lastRefreshedAt };
}